// React component

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Rocket, Zap } from "lucide-react";
import Container from "../global/container";
import { Button } from "../ui/button";
import Particles from "../ui/particles";

const CTA = () => {
  return (
    <div
      className="relative flex flex-col items-center justify-center w-full py-20"
      data-oid="0cfac7h"
    >
      <Container className="py-20 max-w-6xl mx-auto" data-oid="ptnvtqj">
        <div
          className="relative flex flex-col items-center justify-center py-12 lg:py-24 px-6 lg:px-12 rounded-[2.5rem] bg-gradient-to-br from-background/80 via-background/60 to-background/40 backdrop-blur-sm text-center border border-foreground/10 shadow-xl overflow-hidden"
          data-oid="o-mxju-"
        >
          {/* Interactive particles background */}
          <Particles
            refresh
            ease={100}
            quantity={100}
            color="#d4d4d8"
            className="hidden lg:block absolute inset-0 z-0"
            data-oid="4jd03en"
          />

          <Particles
            refresh
            ease={80}
            quantity={40}
            color="#d4d4d8"
            className="block lg:hidden absolute inset-0 z-0"
            data-oid="b9nyyag"
          />

          {/* Animated background gradient */}
          <motion.div
            className="absolute -bottom-1/4 left-1/3 -translate-x-1/2 w-64 h-48 lg:h-72 lg:w-1/2 rounded-full blur-[6rem] lg:blur-[12rem] -z-10"
            style={{
              background:
                "conic-gradient(from 30deg at 50% 50%, #a855f7 0deg, #3b82f6 120deg, #06b6d4 240deg, #8b5cf6 360deg)",
            }}
            animate={{
              rotate: 360,
              scale: [1, 1.1, 1],
            }}
            transition={{
              rotate: {
                duration: 15,
                repeat: Infinity,
                ease: "linear",
              },
              scale: {
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut",
              },
            }}
            data-oid="ufqu6cc"
          />

          {/* Floating decoration elements */}
          <motion.div
            className="absolute top-12 right-16 hidden lg:block"
            animate={{
              y: [0, -10, 0],
              rotate: [0, 5, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            data-oid="4jz3v.a"
          >
            <Rocket className="h-10 w-10 text-blue-500/70" data-oid="pc52un." />
          </motion.div>

          <motion.div
            className="absolute bottom-16 left-16 hidden lg:block"
            animate={{
              y: [0, 10, 0],
              rotate: [0, -5, 0],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            data-oid="3l4m.1q"
          >
            <Sparkles
              className="h-8 w-8 text-purple-500/70"
              data-oid="-1s7udx"
            />
          </motion.div>

          {/* Main content with animated reveal */}
          <motion.div
            className="relative z-10 max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            data-oid="udr00.n"
          >
            <div className="mb-6 flex justify-center" data-oid="ioiy9.m">
              <motion.div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-foreground/5 border border-foreground/10"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.5 }}
                data-oid="20b9vk-"
              >
                <Zap className="h-4 w-4 text-blue-500" data-oid="buz3uxk" />
                <span
                  className="text-sm font-medium text-foreground/80"
                  data-oid="1mjguba"
                >
                  Boost your ROI by up to 300%
                </span>
              </motion.div>
            </div>

            <h2
              className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold !leading-tight bg-clip-text text-transparent bg-gradient-to-r from-foreground via-foreground/90 to-foreground/80"
              data-oid="e4lnmkn"
            >
              Ready to{" "}
              <span className="font-heading" data-oid="k_fx8ev">
                revolutionize
              </span>{" "}
              your <br data-oid="tmuydh2" /> marketing strategy?
            </h2>

            <p
              className="text-sm md:text-lg text-center text-accent-foreground/80 max-w-2xl mx-auto mt-6"
              data-oid="s:kv_bv"
            >
              Transform your marketing with AI-powered automation. Create
              campaigns faster, generate better content{" "}
              <span className="hidden lg:inline" data-oid="9u7x372">
                and make smarter decisions in minutes.
              </span>
            </p>

            <div
              className="mt-10 flex flex-col md:flex-row items-center justify-center gap-4"
              data-oid="m.f-lq1"
            >
              <Link
                to="/demo"
                className="w-full sm:w-auto"
                data-oid="oh6bz-0"
              >
                <Button
                  size="lg"
                  className="w-full sm:w-auto px-8 py-6 text-base rounded-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-600 shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 transition-all duration-300"
                  data-oid="c3fc8pa"
                >
                  <span data-oid="6pt.f3c">Get started now</span>
                  <ArrowUpRight className="ml-2 h-5 w-5" data-oid="3o2ykl-" />
                </Button>
              </Link>
              <Link to="/demo" className="w-full sm:w-auto" data-oid="5:y05p1">
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full sm:w-auto px-8 py-6 text-base rounded-full border-foreground/20 hover:bg-foreground/5 transition-all duration-300"
                  data-oid=".uvkquv"
                >
                  Schedule a demo
                </Button>
              </Link>
            </div>

            <p className="mt-6 text-sm text-foreground/60" data-oid="g3rc2st">
              <span
                className="bg-foreground/5 px-2 py-1 rounded"
                data-oid="0som-wq"
              >
                No credit card required
              </span>{" "}
              •{" "}
              <span className="font-medium" data-oid="f8-lrx6">
                14-day free trial
              </span>
            </p>
          </motion.div>
        </div>
      </Container>
    </div>
  );
};

export default CTA;
